import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import type { Project } from '@/types'

export interface UseOnboardingReturn {
  showOnboarding: boolean
  showChecklist: boolean
  currentStep: number
  onboardingProjectId: string | null
  completeStep: (step: number) => Promise<void>
  skipOnboarding: () => Promise<void>
  completeOnboarding: () => Promise<void>
  dismissChecklist: () => void
}

export function useOnboarding(projects: Project[], loading: boolean): UseOnboardingReturn {
  const { user, refreshUser } = useAuth()
  const [skippedThisSession, setSkippedThisSession] = useState(false)
  const [checklistDismissed, setChecklistDismissed] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)

  // Restore step from user data
  useEffect(() => {
    if (user?.onboarding_step && user.onboarding_step > 0) {
      setCurrentStep(user.onboarding_step)
    }
  }, [user?.onboarding_step])

  const notCompleted = !user?.onboarding_completed_at
  const noProjects = projects.length === 0

  // Find an in-progress dashboard onboarding project created by this user
  const onboardingProject = useMemo(() => {
    if (!user) return null
    return projects.find(
      p => p.onboarding_status === 'dashboard_onboarding' && p.created_by === user.id
    ) ?? null
  }, [projects, user])

  const onboardingProjectId = onboardingProject?.id ?? null

  // Show onboarding when: user hasn't completed it AND (no projects OR has an in-progress onboarding project)
  const showOnboarding = !loading && notCompleted && !skippedThisSession && (noProjects || !!onboardingProject)
  const showChecklist = !loading && notCompleted && !onboardingProject && noProjects && skippedThisSession && !checklistDismissed

  const completeStep = useCallback(async (step: number) => {
    if (!user) return
    const nextStep = step + 1
    setCurrentStep(nextStep)
    await supabase
      .from('users')
      .update({ onboarding_step: nextStep })
      .eq('id', user.id)
  }, [user])

  const skipOnboarding = useCallback(async () => {
    setSkippedThisSession(true)
    // If there's an in-progress onboarding project, mark it complete
    if (onboardingProjectId) {
      await supabase
        .from('projects')
        .update({ onboarding_status: 'complete' })
        .eq('id', onboardingProjectId)
    }
  }, [onboardingProjectId])

  const completeOnboarding = useCallback(async () => {
    if (!user) return
    await supabase
      .from('users')
      .update({ onboarding_completed_at: new Date().toISOString(), onboarding_step: 4 })
      .eq('id', user.id)
    await refreshUser()
  }, [user, refreshUser])

  const dismissChecklist = useCallback(() => {
    setChecklistDismissed(true)
  }, [])

  return {
    showOnboarding,
    showChecklist,
    currentStep,
    onboardingProjectId,
    completeStep,
    skipOnboarding,
    completeOnboarding,
    dismissChecklist,
  }
}
